import * as React from "react";

// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import type { IKeytipProps, ITheme } from "@fluentui/react";
// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import { BaseButton, FocusZone, FocusZoneDirection } from "@fluentui/react";
import type { IContextualMenuItem } from "@fluentui/react/lib/ContextualMenu";
import type { IFocusZoneProps } from "@fluentui/react/lib/FocusZone";
import type { IOverflowSet } from "@fluentui/react/lib/OverflowSet";
import { OverflowSet } from "@fluentui/react/lib/OverflowSet";
import {
  BaseComponent,
  memoizeFunction,
  nullRender,
  warn,
} from "@fluentui/react/lib/Utilities";

import type { WithKey } from "@1js/acui-common";
import { doNotAllowFocusEventToPropagate, InputMethod } from "@1js/acui-common";
import { AppContext } from "@1js/acui-context";
import { iconNameToIconProps } from "@1js/acui-icons";
import { defaultKeytipExecute, deriveTabKeytip } from "@1js/acui-keytip";
import { AppThemeConsumer } from "@1js/acui-theme";
import { AppTooltipHost } from "@1js/acui-tooltip";

import type { PivotHeaderExecutePayload } from "../PivotHeader";
import { PivotHeader } from "../PivotHeader";
import { deriveTabMenuKeytip } from "./deriveTabMenuKeytip";
import { getOverflowMenuClassNames } from "./Pivot.classNames";
import type { IPivotHeader, IPivotProps } from "./Pivot.Props";
import { PivotDirection } from "./Pivot.Props";
import {
  getPivotHeaderOverflowButtonStyles,
  getPivotStyles,
} from "./Pivot.styles";
import {
  getOverflowPivotHeaderId,
  pivotOverflowButtonId,
} from "./PivotConstants";

function addKeyToPivotHeaders(items: IPivotHeader[]): WithKey<IPivotHeader>[] {
  return items.map((item) => ({ ...item, key: item.id }));
}

export class Pivot extends BaseComponent<IPivotProps, {}> {
  private overflowSet = React.createRef<IOverflowSet>();
  private overflowButton = React.createRef<BaseButton>();
  private overflowButtonTooltipWrapper = React.createRef<HTMLDivElement>();
  private selectedPivotId: string | undefined;
  private tooltipHost: AppTooltipHost | undefined;
  private keytipHeaderToExecute: string | undefined = undefined;
  private nullOverflowButtonRenderer = () => {
    return null;
  };

  // This allows us to access AppContext directly from this.context
  static contextType: any = AppContext;
  declare context: React.ContextType<typeof AppContext>;

  constructor(props: IPivotProps) {
    super(props);

    this.selectedPivotId = props.selectedPivotId;

    if (this.props.menuProps && this.props.menuProps.items) {
      warn(
        "Pivot: Do not set items on menuProps because it will be overridden"
      );
    }
  }

  public render() {
    const {
      topLevelItems,
      overflowItems,
      keytipProps,
      label,
      selectedPivotId,
      focusZoneProps,
      direction,
      styles,
    } = this.props;

    const pivotStyles = getPivotStyles(styles);
    this.selectedPivotId = selectedPivotId;

    const customFocusZoneProps: IFocusZoneProps = {
      "aria-label": label,
      isCircularNavigation: true,
      doNotAllowFocusEventToPropagate,
      ...focusZoneProps,
    };

    // all PivotHeaders need keys. The items not in the overflow set also need aria set positioning attributes where position
    // is relative to just the visible tabs (those not in the well) and the set size will include all tabs.
    // items in the overflow set will get separate set position and size attributes relative only to the well from the
    // contextual menu that renders them
    const topLevelItemsWithAriaPositioning = addKeyToPivotHeaders(
      topLevelItems
    ).map(
      (item, idx): WithKey<IPivotHeader> => ({
        ...item,
        ariaPosInSet: idx + 1,
        ariaSetSize:
          topLevelItems.length + (overflowItems ? overflowItems.length : 0),
      })
    );

    const isVertical = direction === PivotDirection.Vertical;
    return (
      <AppThemeConsumer>
        {(theme: ITheme) => (
          <div className={pivotStyles.root}>
            <FocusZone
              id="tablist"
              {...customFocusZoneProps}
              direction={
                isVertical
                  ? FocusZoneDirection.vertical
                  : FocusZoneDirection.horizontal
              }
            >
              <OverflowSet
                componentRef={this.overflowSet}
                items={topLevelItemsWithAriaPositioning}
                overflowItems={addKeyToPivotHeaders(overflowItems || [])}
                onRenderOverflowButton={
                  this.getOverflowButton?.(theme) ||
                  this.nullOverflowButtonRenderer
                }
                onRenderItem={this.getPivotHeader}
                role="tablist"
                keytipSequences={keytipProps && keytipProps.keySequences}
                vertical={isVertical}
              />
            </FocusZone>
          </div>
        )}
      </AppThemeConsumer>
    );
  }

  public componentDidMount() {
    this.updateFancyTooltipHost();
  }

  public componentDidUpdate() {
    this.updateFancyTooltipHost();
  }

  public componentWillUnmount() {
    this.getOverflowButton = undefined;
  }

  public focus = () => {
    if (!this.overflowSet.current) {
      return;
    }

    const selectedTab = this.selectedPivotId
      ? document.querySelector(`#${this.selectedPivotId}`)
      : undefined;

    if (selectedTab) {
      this.overflowSet.current.focusElement(selectedTab as HTMLElement);
    } else {
      this.overflowSet.current.focus(true /* forceIntoFirstChild */);
    }
  };

  private getPivotHeader = (header: WithKey<IPivotHeader>) => {
    const pivotHeaderIsSelected = header.id === this.props.selectedPivotId;
    return (
      <PivotHeader
        {...header}
        onPivotHeaderExecute={this.onPivotHeaderExecute}
        selectedPivotHeader={pivotHeaderIsSelected}
        appColors={this.props.appColors}
        onMouseEnter={this.props.onPivotHeaderMouseEnter}
        onMouseLeave={this.props.onPivotHeaderMouseLeave}
        onMouseDown={this.props.onPivotHeaderMouseDown}
        onMouseUp={this.props.onPivotHeaderMouseUp}
        onClick={this.props.onPivotHeaderClick}
        onBlur={this.props.onPivotHeaderBlur}
        onFocus={this.props.onPivotHeaderFocus}
        displayTabIndicatorPlaceholder={
          this.props.displayTabIndicatorPlaceholder && pivotHeaderIsSelected
        }
        useNextStyles={this.props.useNextStyles}
        automationType={this.props.automationType}
        hideSelectedTabIndicators={this.props.hideSelectedTabIndicators}
      />
    );
  };

  private getOverflowButton:
    | ((theme: ITheme) => (overflowPivotHeaders: IPivotHeader[]) => JSX.Element)
    | undefined = memoizeFunction((theme: ITheme) => {
    return (overflowPivotHeaders: IPivotHeader[]) => {
      const customStyles =
        this.props.styles && this.props.styles.overflowButton;

      return (
        <div ref={this.overflowButtonTooltipWrapper}>
          <BaseButton
            componentRef={this.overflowButton}
            onRenderMenuIcon={nullRender}
            iconProps={{
              iconName: "ChevronDown",
              ...this.props.menuIconProps,
            }}
            data-unique-id={pivotOverflowButtonId}
            id={pivotOverflowButtonId}
            styles={getPivotHeaderOverflowButtonStyles(theme, customStyles)}
            onMenuClick={this.onOverflowButtonExecute}
            menuProps={{
              getMenuClassNames: getOverflowMenuClassNames(
                this.props.appColors
              ),
              ...this.props.menuProps,
              items: overflowPivotHeaders.map((header): IContextualMenuItem => {
                return {
                  id: getOverflowPivotHeaderId(header.id),
                  key: header.id,
                  name: header.title,
                  disabled: header.disabled,
                  iconProps: iconNameToIconProps(header.icon),
                  role: "menuitemradio",
                  onClick: () =>
                    this.onPivotHeaderExecute(header.id, {
                      inputMethod: InputMethod.Mouse,
                    }),
                  getItemClassNames: header.getMenuItemClassNames,
                  keytipProps: deriveTabMenuKeytip(
                    header.keytipProps,
                    (executeTarget: HTMLElement) => {
                      executeTarget && executeTarget.focus();
                      // Save the header to execute after the menu has closed.
                      // This is to prevent errors where the menu keytips are still around
                      // and the bottom bar keytips think the menu keytips are the parent
                      // when instead the parent should be the active top bar tab
                      this.keytipHeaderToExecute = header.id;
                      this.dismissMenu();
                    }
                  ) as IKeytipProps,
                };
              }),
            }}
            keytipProps={
              deriveTabKeytip(
                this.props.keytipProps,
                this.keytipExecute
              ) as IKeytipProps
            }
            title={undefined}
            ariaLabel={this.props.overflowMenuLauncherLabel}
            allowDisabledFocus={true}
            onAfterMenuDismiss={this.executeHeaderForKeytip}
          />
        </div>
      );
    };
  });

  private keytipExecute = (executeTarget: HTMLElement) => {
    defaultKeytipExecute(executeTarget, this.openMenu);
  };

  private onPivotHeaderExecute = (
    id: string,
    payload?: PivotHeaderExecutePayload
  ) => {
    if (this.props.onPivotHeaderExecute) {
      this.props.onPivotHeaderExecute(id, payload);
    }
  };

  private executeHeaderForKeytip = () => {
    // Triggers the tab header execution function, if keytipHeaderToExecute is set.
    // keytipHeaderToExecute gets set when a tab header keytip from the overflow menu is triggered.
    // This function fires after the overflow menu is closed
    if (this.keytipHeaderToExecute) {
      this.onPivotHeaderExecute(this.keytipHeaderToExecute);
      // Unset saved value
      this.keytipHeaderToExecute = undefined;
    }
  };

  private openMenu = () => {
    if (this.overflowButton.current) {
      this.overflowButton.current.openMenu();
    }
  };

  private dismissMenu = () => {
    if (this.overflowButton.current) {
      this.overflowButton.current.dismissMenu();
    }
  };

  private onOverflowButtonExecute = () => {
    if (this.props.onOverflowButtonExecute) {
      this.props.onOverflowButtonExecute();
    }
  };

  private updateFancyTooltipHost = () => {
    if (this.props.overflowItems && this.props.overflowItems.length > 0) {
      this.addFancyTooltipToOverflowButton();
    } else {
      this.removeFancyTooltipTFromOverflowButton();
    }
  };

  private addFancyTooltipToOverflowButton = () => {
    if (
      this.context.setTooltipElement &&
      this.context.updateTooltipsHidden &&
      this.overflowButtonTooltipWrapper.current &&
      !this.tooltipHost
    ) {
      this.tooltipHost = new AppTooltipHost({
        updateTooltipsHidden: this.context.updateTooltipsHidden,
        setTooltipElement: this.context.setTooltipElement,
        htmlElement: this.overflowButtonTooltipWrapper.current,
        controlId: this.props.overflowMenuLauncherLabel,
        controlLabel: this.props.overflowMenuLauncherLabel,
        tooltipDirection: this.props.tooltipDirection,
        performSetOnNextFrame: true,
      });
      this.tooltipHost.registerTooltipHostActions();
    }
  };

  private removeFancyTooltipTFromOverflowButton = () => {
    if (
      this.context.setTooltipElement &&
      this.context.updateTooltipsHidden &&
      this.tooltipHost
    ) {
      this.tooltipHost.unregisterTooltipHostActions();
      this.tooltipHost = undefined;
    }
  };
}
